<template>
    <div
        v-if="index > 0"
        class="h-2 surface-secondary"
    />
    <div
        ref="widget"
        :data-cy="`simple-widget-type-${widgetType}`"
    >
        <slot
            v-if="hasHeader"
            name="header"
        >
            <div class="flex items-center justify-between pt-5 mb-4 px-4">
                <span class="content-sm-ux-medium text-neutral-base">{{ title }}</span>
                <slot name="title-after" />
            </div>
        </slot>

        <DependencyErrorAlert
            v-if="!isLoading"
            class="my-4"
            :widget-type="widgetType"
            :dependencies="dependencies"
            @retry="emit('retry')"
        />
        <div class="relative">
            <template v-if="isLoading || isEmpty">
                <!-- Use visibility hidden so that the empty state height matches the loading state and reduces visual jump -->
                <div :class="{ invisible: !isLoading }">
                    <slot name="placeholder-content">
                        <div
                            v-for="n in placeholderItemCount"
                            :key="n"
                            class="mx-4"
                        >
                            <slot
                                name="placeholder-item"
                                :is-last="n === placeholderItemCount"
                            />
                        </div>
                    </slot>
                </div>
                <div
                    v-if="!isLoading"
                    class="absolute left-0 right-0 border-0 top-0 bottom-0"
                >
                    <slot name="empty">
                        <Empty class="h-full" />
                    </slot>
                </div>
            </template>
            <template v-else>
                <slot />
            </template>
        </div>
    </div>
</template>

<script setup>
    import mitt from 'mitt'

    import { useWidgetsStore } from '@/stores/widgets'
    import Empty from '@/components/ui/Empty.vue'

    const emit = defineEmits(['retry'])

    const props = defineProps({
        widgetType: String,
        dependencies: Array,
        index: {
            type: Number,
            default: 0,
        },
        title: String,
        placeholderItemCount: {
            type: Number,
            default: 5,
        },
        hasHeader: {
            type: Boolean,
            default: true,
        },
        loading: Boolean,
        // Sometimes a widget will have data in the dependencies, but can't show it e.g. date out of range for the today widget
        forceEmpty: Boolean,
    })

    const widgetHelpers = useWidgetHelpers()

    const widgetsStore = useWidgetsStore()

    const el = useTemplateRef('widget')
    const shouldRefresh = ref(false)

    const contextualEvents = inject('contextualEvents', ref(mitt()))
    provide('contextualEvents', contextualEvents)

    const dependencyStatuses = computed(() => {
        return widgetsStore.dependencies(props.widgetType)
    })

    const errors = computed(() => {
        const errors = []
        each(props.dependencies, (dependency) => {
            const config = dependencyStatuses.value[dependency.url]
            if (!config || !config.error) {
                return
            }
            errors.push(config.error)
        })
        return errors
    })

    const isLoading = computed(() => {
        return props.loading || widgetsStore.isLoading(props.widgetType, props.dependencies)
    })

    const isEmpty = computed(() => {
        return widgetsStore.items(props.widgetType, props.dependencies).length === 0
            || props.forceEmpty
    })

    const retry = () => {
        // Retry only the dependencies that failed
        widgetHelpers.fetchDependenciesHandlingRestartIfRequired(props.widgetType, filter(props.dependencies, (dependency) => {
            const config = dependencyStatuses.value[dependency.url]
            return !config || config.error
        }))

        emit('retry')
    }

    const fetchDependencies = (refresh = false) => {
        widgetHelpers.fetchDependenciesHandlingRestartIfRequired(props.widgetType, props.dependencies, refresh)
        shouldRefresh.value = false
    }

    const { pause: pauseObserver, resume: resumeObserver } = useIntersectionObserver(el, ([entry]) => {
        const isVisible = entry?.isIntersecting ?? false
        if (!isVisible) {
            return
        }
        // Pause the observer as we only need to know when it becomes visible
        pauseObserver()
        fetchDependencies(shouldRefresh.value)
    })

    const markForRefresh = () => {
        shouldRefresh.value = true
        resumeObserver()
    }

    const refreshIfNeeded = () => {
        // Call fetch dependencies without specifying refresh
        // This will retry failing dependencies and re-fetch dependencies marked for refresh
        resumeObserver()
    }

    watch(() => props.dependencies, () => {
        fetchDependencies()
    })

    onMounted(() => {
        events.on('refreshed-space', markForRefresh)
        events.on('space-tab-changed', refreshIfNeeded)
        contextualEvents.value.on('refresh', markForRefresh)

    })

    onUnmounted(() => {
        events.off('refreshed-space', markForRefresh)
        events.off('space-tab-changed', refreshIfNeeded)
        contextualEvents.value.off('refresh', markForRefresh)
    })
</script>
